<script lang="ts" setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { entries } from 'lodash';
import type { ColumnSchema, ColumnSchemaModel } from '@/modules/core/common/services/GridService';


const props = defineProps({
  "columns": null,
  "portalName": null,
  "title": null,
  "summaryProp": null
});


const emit = defineEmits(["definitionChanged"]);

const columnDef = ref<ColumnSchemaModel | null>(props.columns);
const allSummaryCheckbox = ref(false);


const isNumericColumn = (column: ColumnSchema) =>
{
    return ['Decimal', 'Integer', 'Number'].includes(column.type);
};

const canShowSummary = (column: ColumnSchema) =>
{
    return isNumericColumn(column) && column.visibility;
};


const summaryColumns = computed<[string, ColumnSchema][]>(() =>
{
    columnDef.value = props.columns;

    if (columnDef.value)
    {
        return entries(columnDef.value)
            .sort((a, b) =>
            {
                if (b[1].order < a[1].order) return 1;

                return -1;
            })
            .filter((column) =>
                column[0] !== 'id' &&
                column[0] !== 'publicId' &&
                column[0] !== 'userId' &&
                column[1].headerName &&
                canShowSummary(column[1])
            );
    }

    return [];
});


const columnSummaryChanged = (value: boolean, item: ColumnSchema) =>
{
    (item as any)[props.summaryProp] = value;
    emit('definitionChanged', columnDef.value!);
};

const changeAllSummaryCheckbox = (value: boolean) =>
{
    allSummaryCheckbox.value = value;

    entries(columnDef.value!).forEach(([_, column]) =>
    {
        if (canShowSummary(column))
        {
            (column as any)[props.summaryProp] = value;
        }
    });

    emit('definitionChanged', columnDef.value!);
};


watch(
    () => props.columns,
    (newColumns) =>
    {
        columnDef.value = newColumns;
    }
);

watch(
    summaryColumns,
    (columns) =>
    {
        if (columns.length > 0)
        {
            allSummaryCheckbox.value = columns.some(([_, column]) => column[props.summaryProp]);
        }
        else
        {
            allSummaryCheckbox.value = false;
        }
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <portal :to="props.portalName">
        <data-card overflow scroll class="overflow-hidden flex-shrink-1">
            <template #header>
                <div class="d-flex justify-content-between">
                    <ideo-form-checkbox :model-value="allSummaryCheckbox" @change="changeAllSummaryCheckbox">
                        <strong>{{ $t(props.title) }}</strong>
                    </ideo-form-checkbox>
                </div>
            </template>

            <template #default>
                <div class="summary-customizer scroll scroll--overflow-x-hidden">
                    <div v-for="([key, header], index) in summaryColumns" :key="index" class="dropdown-item px-3 py-2">
                        <div class="d-flex align-items-center">
                            <ideo-form-checkbox
                                :model-value="header[props.summaryProp] || false"
                                @change="(value: boolean) => columnSummaryChanged(value, header)"
                            >
                                {{ header.headerName }}
                            </ideo-form-checkbox>
                        </div>
                    </div>

                    <template v-if="!summaryColumns.length">
                        <p class="mb-0 p-2 px-3">{{ $t('[[[Brak kolumn numerycznych]]]') }}</p>
                    </template>
                </div>
            </template>
        </data-card>
    </portal>
</template>

<style lang="scss" scoped>
.summary-customizer {
  margin: -10px;

  .card-header {
    padding: 0.5rem 1rem;
  }

  .dropdown-item {
    &:last-of-type {
      border-bottom: 0;
    }
  }
}
</style>
